/* Routing and the session gate.
 *
 * One decision here worth naming: someone with no session sees the sign-in
 * page, and someone whose session expires mid-use is returned to it by the
 * SESSION_EXPIRED event the API client raises. The client cannot navigate on
 * its own — it does not know about routes — so the shell listens instead.
 */
import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { AppShell } from '@/components/AppShell'
import { FeedPage } from '@/features/feed/FeedPage'
import { CallRoomPage } from '@/features/church/CallRoomPage'
import { LivePage } from '@/features/church/LivePage'
import { ComingUpPage } from '@/features/church/ComingUpPage'
import { CallReportPage } from '@/features/church/CallReportPage'
import { CallsPage } from '@/features/church/CallsPage'
import { MessagesPage } from '@/features/church/MessagesPage'
import { MessageReportPage } from '@/features/church/MessageReportPage'
import { NotificationSettingsPage } from '@/features/account/NotificationSettingsPage'
import { ChurchDetailsPage } from '@/features/account/ChurchDetailsPage'
import { ProfilePage } from '@/features/account/ProfilePage'
import { JoinQuestionsPage } from '@/features/church/JoinQuestionsPage'
import { PrayerPage } from '@/features/prayer/PrayerPage'
import { FollowingPage } from '@/features/prayer/FollowingPage'
import { HelpMatchesPage } from '@/features/prayer/HelpMatchesPage'
import { MembersPage } from '@/features/church/MembersPage'
import { FindChurchPage } from '@/features/church/FindChurchPage'
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage'
import { SignInPage } from '@/features/auth/SignInPage'
import { HandoffPage } from '@/features/auth/HandoffPage'
import { SignUpPage } from '@/features/auth/SignUpPage'
import { InvitePage } from '@/features/church/InvitePage'
import { GalleryPage } from '@/features/dev/GalleryPage'
import { GroupDetailPage } from '@/features/church/GroupDetailPage'
import { GroupsPage } from '@/features/church/GroupsPage'
import { PermissionsPage } from '@/features/church/PermissionsPage'
import { ServicesPage } from '@/features/attendance/ServicesPage'
import {
  AttendancePage,
  MarkPresentPage,
  ServiceDaysPage,
} from '@/features/attendance/AttendancePage'
import { ServiceDetailPage } from '@/features/attendance/ServiceDetailPage'
import { DayReportPage } from '@/features/attendance/DayReportPage'
import { FamilyPage } from '@/features/account/FamilyPage'
import { SESSION_EXPIRED, tokens } from '@/api/client'
import { useMe } from '@/api/queries'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Coming back to the tab after a while should show what happened while
      // you were away. `staleTime` still gates it, so flicking between windows
      // costs nothing — only a genuinely stale feed refetches.
      refetchOnWindowFocus: true,
      // Auth failures are terminal — retrying a 401 three times just delays
      // the sign-in screen.
      retry: (count, err: unknown) => {
        const status = (err as { status?: number })?.status
        if (status === 401 || status === 403 || status === 404) return false
        return count < 2
      },
    },
  },
})

function Protected({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, isError } = useMe()

  if (!tokens.access) return <SignInPage />
  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-ink-3" aria-label="Loading" />
      </div>
    )
  }
  // A stored token that the server rejects is no session at all.
  if (isError || !me) return <SignInPage />

  return <AppShell>{children}</AppShell>
}

function Router() {
  const [, force] = useState(0)

  useEffect(() => {
    // The API client raises this when a refresh fails for good. Re-rendering
    // drops the app back to the sign-in page without a hard reload.
    function onExpired() {
      queryClient.clear()
      force((n) => n + 1)
    }
    window.addEventListener(SESSION_EXPIRED, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired)
  }, [])

  return (
    <Routes>
      {/* PUBLIC, above the protected routes. Somebody creating an account has
          no session by definition, so these must not sit behind <Protected>.

          /signup/:slug is a church's invite link — the same form, but the
          person ends up attached to that church. */}
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/signup/:slug" element={<SignUpPage />} />
      {/* A group invitation. PUBLIC, because the whole point is that somebody
          with no account can open it, see what they were invited to, and sign
          up from there. */}
      <Route path="/invite/:token" element={<InvitePage />} />
      {/* Protected renders SignInPage inline rather than redirecting, so this
          route exists only to give "Sign in" links somewhere to point. */}
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/handoff" element={<HandoffPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      <Route path="/" element={<Protected><FeedPage /></Protected>} />
      <Route path="/prayer" element={<Protected><PrayerPage /></Protected>} />
      {/* Testimonies — the same feed, narrowed to answered prayers. */}
      <Route
        path="/testimonies"
        element={
          <Protected>
            <FeedPage
              postType="testimony"
              empty={{
                title: 'No testimonies yet',
                body: 'When a Prayer Request is answered, the person who raised it can share the story here.',
              }}
            />
          </Protected>
        }
      />
      <Route
        path="/prayer/following"
        element={<Protected><FollowingPage /></Protected>}
      />
      <Route
        path="/prayer/help"
        element={<Protected><HelpMatchesPage /></Protected>}
      />
      <Route path="/groups" element={<Protected><GroupsPage /></Protected>} />
      <Route path="/members" element={<Protected><MembersPage /></Protected>} />
      <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
      <Route path="/services" element={<Protected><ServicesPage /></Protected>} />
      <Route
        path="/services/:serviceId"
        element={<Protected><ServiceDetailPage /></Protected>}
      />
      <Route path="/attendance" element={<Protected><AttendancePage /></Protected>} />
      <Route path="/attendance/mark" element={<Protected><MarkPresentPage /></Protected>} />
      <Route path="/attendance/days" element={<Protected><ServiceDaysPage /></Protected>} />
      <Route
        path="/attendance/days/:occurrenceId"
        element={<Protected><DayReportPage /></Protected>}
      />
      <Route path="/settings/family" element={<Protected><FamilyPage /></Protected>} />
      <Route path="/calls" element={<Protected><CallsPage /></Protected>} />
      <Route path="/coming-up" element={<Protected><ComingUpPage /></Protected>} />
      <Route path="/calls/:callId/room" element={<Protected><CallRoomPage /></Protected>} />
      <Route path="/calls/:callId/live" element={<Protected><LivePage /></Protected>} />
      <Route path="/calls/:callId/report" element={<Protected><CallReportPage /></Protected>} />
      <Route
        path="/messages/:messageId"
        element={<Protected><MessageReportPage /></Protected>}
      />
      <Route
        path="/settings/notifications"
        element={<Protected><NotificationSettingsPage /></Protected>}
      />
      <Route
        path="/join-questions"
        element={<Protected><JoinQuestionsPage /></Protected>}
      />
      <Route path="/settings/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route
        path="/settings/church"
        element={<Protected><ChurchDetailsPage /></Protected>}
      />
      <Route path="/find-church" element={<Protected><FindChurchPage /></Protected>} />
      <Route
        path="/groups/:groupId"
        element={<Protected><GroupDetailPage /></Protected>}
      />
      <Route
        path="/permissions"
        element={<Protected><PermissionsPage /></Protected>}
      />
      {/* The old combined Administration screen is gone: Groups is its own
          list now, and members are reached through the group they belong to.
          Anyone with the old link lands on the list. */}
      <Route path="/administration" element={<Navigate to="/groups" replace />} />
      {/* Component gallery. Not linked from the product — it exists so every
          field state can be checked at once. */}
      <Route path="/gallery" element={<GalleryPage />} />
      {/* Anything unrecognised goes home rather than showing a dead end. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Routes are relative to wherever the app is mounted — the root on its
          own subdomain, /app/ on the legacy host. Vite injects the base it was
          built with, so the two cannot drift apart. */}
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <Router />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
